import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "../providers/AuthProvider";

// Layouts (van en src/app/layout/)
import MainLayout from "../layout/MainLayout";
import AuthLayout from "../layout/AuthLayout";

// Public pages (no requieren autenticación)
import LoginPage from "../../features/auth/pages/LoginPage";
import PasswordResetRequestPage from "../../features/auth/pages/PasswordResetRequestPage";

// Inicio
import DashboardInicioPage from "../../features/inicio/pages/DashboardInicioPage";
import ConsultantLayout from "../../features/consultora/components/ConsultantLayout";
import ConsultantHomePage from "../../features/consultora/pages/ConsultantHomePage";
import GlobalIngredientsPage from "../../features/consultora/pages/GlobalIngredientsPage";

// Stock
import StockPage from "../../features/stock/pages/StockPage";
import StockAreaPage from "../../features/stock/pages/StockAreaPage";
import StockSubsectionPage from "../../features/stock/pages/StockSubsectionPage";
import PreparedProductRecipeDetailPage from "../../features/stock/baseDatos/elaborados/pages/PreparedProductRecipeDetailPage";
import RecipeDetailPage from "../../features/stock/baseDatos/recetas/pages/RecipeDetailPage";
import CreateRecipePage from "../../features/stock/baseDatos/recetas/pages/CreateRecipePage";
import EditRecipePage from "../../features/stock/baseDatos/recetas/pages/EditRecipePage";
import CreatePreparedProductPage from "../../features/stock/baseDatos/elaborados/pages/CreatePreparedProductPage";
import EditPreparedProductPage from "../../features/stock/baseDatos/elaborados/pages/EditPreparedProductPage";
import CreateSaleProductPage from "../../features/stock/baseDatos/productosVenta/pages/CreateSaleProductPage";
import EditSaleProductPage from "../../features/stock/baseDatos/productosVenta/pages/EditSaleProductPage";
import PurchaseDetailPage from "../../features/stock/operacion/compras/pages/PurchaseDetailPage";
import CreatePurchaseOrderPage from "../../features/stock/operacion/compras/pages/CreatePurchaseOrderPage";
import CreateProductionPage from "../../features/stock/operacion/produccionElaborados/pages/CreateProductionPage";
import StockCountDetailPage from "../../features/stock/operacion/conteoDeStock/pages/StockCountDetailPage";
import ImportSalesWizardPage from "../../features/stock/operacion/ventasImportadas/pages/ImportSalesWizardPage";
import SalesImportDetailPage from "../../features/stock/operacion/ventasImportadas/pages/SalesImportDetailPage";
import PurchaseItemsWorkspacePage from "../../features/stock/baseDatos/itemsDeCompra/pages/PurchaseItemsWorkspacePage";
import BaseDatosLayout from "../../features/stock/baseDatos/components/BaseDatosLayout";
import InsumosPage from "../../features/stock/baseDatos/insumos/pages/InsumosPage";
import ElaboradosPage from "../../features/stock/baseDatos/elaborados/pages/ElaboradosPage";
import ProductosVentaPage from "../../features/stock/baseDatos/productosVenta/pages/ProductosVentaPage";
import RecetasPage from "../../features/stock/baseDatos/recetas/pages/RecetasPage";
import {
  canAccessStockSubsection,
  getStockSubsectionByPath,
} from "../../features/stock/constants/stockAreas";

// Tesorería
import TesoreriaHomePage from "../../features/tesoreria/pages/TesoreriaHomePage";
import TesoreriaCrearCierrePage from "../../features/tesoreria/lienzo_de_trabajo/pages/TesoreriaCrearCierrePage";
import TesoreriaLienzoTrabajoPage from "../../features/tesoreria/lienzo_de_trabajo/pages/TesoreriaLienzoTrabajoPage";
import TesoreriaHistorialCierresPage from "../../features/tesoreria/historial/pages/TesoreriaHistorialCierresPage";
import TesoreriaFormulariosPage from "../../features/tesoreria/formularios/pages/TesoreriaFormulariosPage";
import TesoreriaFormulariosCrearPage from "../../features/tesoreria/formularios/pages/TesoreriaFormulariosCrearPage";
import TesoreriaFormulariosEditarPage from "../../features/tesoreria/formularios/pages/TesoreriaFormulariosEditarPage";
import TesoreriaFormulariosGestionarPage from "../../features/tesoreria/formularios/pages/TesoreriaFormulariosGestionarPage";

// Privat pages (RRHH)
import DashboardPage from "../../features/rrhh/dashboard/pages/DashboardPage";
import EmpleadosPage from "../../features/rrhh/empleados/pages/EmpleadosPage";
import HorariosPage from "../../features/rrhh/horarios/pages/HorariosPage";
import AsistenciaPage from "../../features/rrhh/asistencia/pages/AsistenciaPage";
import AsistenciaDetallePage from "../../features/rrhh/asistencia/pages/AsistenciaDetallePage";
import TareasPage from "../../features/rrhh/tareas/pages/TareasPage";
import ConfiguracionesPage from "../../features/configuraciones/pages/ConfiguracionesPage";
import LiquidacionPage from "../../features/rrhh/liquidacion/pages/LiquidacionPage";
import CreateClosureWizardPage from "../../features/rrhh/liquidacion/pages/CreateClosureWizardPage";
import ClosureUsersSelectionPage from "../../features/rrhh/liquidacion/pages/ClosureUsersSelectionPage";
import ClosurePayrollDataPage from "../../features/rrhh/liquidacion/pages/ClosurePayrollDataPage";
import MensajesPage from "../../features/rrhh/mensajeria/pages/MensajesPage";
import ClosureAccountingDataPage from "../../features/rrhh/liquidacion/pages/ClosureAccountingDataPage";
import ClosureConfirmationPage from "../../features/rrhh/liquidacion/pages/ClosureConfirmationPage";
import MissingClosuresPage from "../../features/rrhh/liquidacion/pages/MissingClosuresPage";
import PaymentHistoryReportPage from "../../features/rrhh/liquidacion/pages/PaymentHistoryReportPage";
import ContenidoPage from "../../features/rrhh/contenido/pages/ContenidoPage";
import ContenidoFormPage from "../../features/rrhh/contenido/pages/ContenidoFormPage";
import ContenidoFiltersCleaner from "../../features/rrhh/contenido/components/ContenidoFiltersCleaner";
import EvaluacionFormPage from "../../features/rrhh/contenido/pages/EvaluacionFormPage";
import EvaluacionResultadosPage from "../../features/rrhh/contenido/pages/EvaluacionResultadosPage";
import EventosPage from "../../features/rrhh/eventos/pages/EventosPage";

export function ProtectedStockSubsectionRoute({
  sectionPath,
  subsectionPath,
  children,
}) {
  const { user } = useAuth();
  const { section, subsection } = getStockSubsectionByPath(
    sectionPath,
    subsectionPath
  );

  if (!section) return <Navigate to="/stock" replace />;
  if (!subsection || !canAccessStockSubsection(subsection, user)) {
    return <Navigate to={`/stock/${section?.path || sectionPath}`} replace />;
  }

  return children;
}

export function RootRedirect() {
  const { loading, isAuthenticated, user } = useAuth();
  if (loading) return null;

  const role = String(user?.user_role || "").toUpperCase();
  return <Navigate to={isAuthenticated && role === "CONSULTORA" ? "/consultora" : "/inicio"} replace />;
}

export default function AppRouter() {
  return (
    <>
      <ContenidoFiltersCleaner />
      
      <Routes>
        {/* Root => Inicio */}
        <Route path="/" element={<RootRedirect />} />

        {/* Public */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/password-reset/request" element={<PasswordResetRequestPage />} />
        </Route>

        {/* Private */}
        <Route
          path="/select-client"
          element={
            <ProtectedRoute>
              <Navigate to="/consultora" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/consultora"
          element={
            <ProtectedRoute allowedRoles={["CONSULTORA"]}>
              <ConsultantLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ConsultantHomePage />} />
          <Route path="insumos-globales" element={<GlobalIngredientsPage />} />
        </Route>

        {/* Private: Inicio (post-login) */}
        <Route
          path="/inicio"
          element={
            <ProtectedRoute requireLicense>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardInicioPage />} />
        </Route>

        {/* Private: RRHH */}
        <Route
          path="/rrhh"
          element={
            <ProtectedRoute requireLicense>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          <Route path="empleados" element={<EmpleadosPage />} />

          <Route path="horarios" element={<HorariosPage />} />

          <Route path="asistencia" element={<AsistenciaPage />} />
          <Route path="asistencia/:employeeId" element={<AsistenciaDetallePage />} />

          <Route path="tareas" element={<TareasPage />} />

          <Route path="liquidacion" element={<LiquidacionPage />} />
          <Route path="liquidacion/faltantes" element={<MissingClosuresPage />} />
          <Route path="liquidacion/crear" element={<CreateClosureWizardPage />} />
          <Route path="liquidacion/reporte-pagos" element={<PaymentHistoryReportPage />} />
          <Route path="liquidacion/:closureId/usuarios" element={<ClosureUsersSelectionPage />} />
          <Route path="liquidacion/:closureId/datos" element={<ClosurePayrollDataPage />} />
          <Route path="liquidacion/:closureId/accounting" element={<ClosureAccountingDataPage />} />
          <Route path="liquidacion/:closureId/reporte" element={<ClosureConfirmationPage />} />

          <Route path="mensajes" element={<MensajesPage />} />

          <Route path="contenido" element={<ContenidoPage />} />
          <Route path="contenido/crear" element={<ContenidoFormPage />} />
          <Route path="contenido/:contentId/editar" element={<ContenidoFormPage />} />
          
          <Route path="contenido/evaluaciones/crear" element={<EvaluacionFormPage />} />
          <Route path="contenido/evaluaciones/:evaluationId/editar" element={<EvaluacionFormPage />} />
          <Route path="contenido/evaluaciones/:evaluationId/resultados" element={<EvaluacionResultadosPage />} />
          <Route path="eventos" element={<EventosPage />} />
        </Route>

        {/* Private: Tesorería */}
        <Route
          path="/tesoreria"
          element={
            <ProtectedRoute requireLicense>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TesoreriaHomePage />} />
          <Route path="lienzo" element={<TesoreriaLienzoTrabajoPage />} />
          <Route path="lienzo/:periodId" element={<TesoreriaLienzoTrabajoPage />} />
          <Route path="lienzo/generar" element={<TesoreriaCrearCierrePage />} />
          <Route path="historial" element={<TesoreriaHistorialCierresPage />} />
          <Route path="historial/:periodId" element={<TesoreriaHistorialCierresPage />} />
          <Route path="formularios" element={<TesoreriaFormulariosPage />} />
          <Route path="formularios/crear" element={<TesoreriaFormulariosCrearPage />} />
          <Route path="formularios/:formId/editar" element={<TesoreriaFormulariosEditarPage />} />
          <Route path="formularios/:formId/gestionar" element={<TesoreriaFormulariosGestionarPage />} />
        </Route>

        {/* Private: Stock */}
        <Route
          path="/stock"
          element={
            <ProtectedRoute requireLicense>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<StockPage />} />
          <Route path=":sectionPath" element={<StockAreaPage />} />
          <Route path="base-de-datos" element={<BaseDatosLayout />}>
            <Route index element={<Navigate to="insumos" replace />} />
            <Route path="insumos" element={<InsumosPage />} />
            <Route path="insumos/presentaciones" element={<PurchaseItemsWorkspacePage />} />
            <Route path="insumos/:ingredientId" element={<Navigate to="../insumos" replace />} />
            <Route path="elaborados" element={<ElaboradosPage />} />
            <Route path="elaborados/crear" element={<CreatePreparedProductPage />} />
            <Route path="elaborados/:preparedProductId/editar" element={<EditPreparedProductPage />} />
            <Route path="elaborados/:preparedProductId/receta" element={<PreparedProductRecipeDetailPage />} />
            <Route path="productos-de-venta" element={<ProductosVentaPage />} />
            <Route path="productos-de-venta/crear" element={<CreateSaleProductPage />} />
            <Route path="productos-de-venta/:saleProductId/editar" element={<EditSaleProductPage />} />
            <Route path="recetas" element={<RecetasPage />} />
            <Route path="recetas/crear" element={<CreateRecipePage />} />
            <Route path="recetas/:recipeId/editar" element={<EditRecipePage />} />
            <Route path="recetas/:recipeId/receta" element={<RecipeDetailPage />} />
            <Route path="proveedores" element={<Navigate to="../insumos" replace />} />
            <Route path="items-de-compra" element={<Navigate to="../insumos" replace />} />
            <Route path="items-de-compra/lienzo" element={<Navigate to="../insumos" replace />} />
          </Route>
          <Route
            path="operacion/compras/crear"
            element={<CreatePurchaseOrderPage />}
          />
          <Route
            path="operacion/compras/:purchaseId"
            element={<PurchaseDetailPage />}
          />
          <Route
            path="operacion/produccion-elaborados/crear"
            element={<CreateProductionPage />}
          />
          <Route
            path="operacion/conteo-de-stock/:stockCountId"
            element={
              <ProtectedStockSubsectionRoute
                sectionPath="operacion"
                subsectionPath="conteo-de-stock"
              >
                <StockCountDetailPage />
              </ProtectedStockSubsectionRoute>
            }
          />
          <Route
            path="operacion/ventas-importadas/importar"
            element={
              <ProtectedStockSubsectionRoute
                sectionPath="operacion"
                subsectionPath="ventas-importadas"
              >
                <ImportSalesWizardPage />
              </ProtectedStockSubsectionRoute>
            }
          />
          <Route
            path="operacion/ventas-importadas/:importId"
            element={
              <ProtectedStockSubsectionRoute
                sectionPath="operacion"
                subsectionPath="ventas-importadas"
              >
                <SalesImportDetailPage />
              </ProtectedStockSubsectionRoute>
            }
          />
          <Route path=":sectionPath/:subsectionPath" element={<StockSubsectionPage />} />
        </Route>

        <Route
          path="/configuraciones"
          element={
            <ProtectedRoute requireLicense>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ConfiguracionesPage />} />
        </Route>

        {/* Not found => Inicio */}
        <Route path="*" element={<Navigate to="/inicio" replace />} />
      </Routes>
    </>
  );
}
